import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Collapse,
  Stack,
} from '@mui/material';
import {
  AssignmentTurnedIn as MyTasksIcon,
  CheckCircleOutline as CompleteIcon,
  PauseCircleOutline as CantFinishIcon,
  Replay as ResumeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as PlanIcon,
  PlayArrow as StartIcon,
  SwapHoriz as SwitchIcon,
  Stop as ClockOutIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getTaskSettings, getTasks, updateTaskStatus, planTask } from '../api';
import ClockCard from './ClockCard';
import { getUserTimezone, formatPlannedDateTime, formatDateInTimezone, getZonedDateTimeParts, zonedWallTimeToUtcIso } from '../utils/formatTime';

// Server response shape for list endpoints in this app isn't fully settled
// between a bare array and a { tasks: [...] } wrapper — accept either.
const unwrapList = (data) => (Array.isArray(data) ? data : (data?.tasks || []));

const PRIORITY_LABELS = { low: 'Low', normal: 'Normal', high: 'High', urgent: 'Urgent' };
const PRIORITY_COLORS = { low: 'default', normal: 'info', high: 'warning', urgent: 'error' };

// Format a "YYYY-MM-DD" (optionally with a time part) date string without
// letting `new Date()` shift it a day via local timezone conversion.
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

// Default time for a brand-new plan: now, rounded up to the next quarter
// hour, expressed as wall-clock date/time in the account's timezone (NOT the
// browser's) — same zone every display on this screen already uses, via
// getZonedDateTimeParts. Rounding the absolute instant to the nearest UTC
// quarter-hour and then reading it back in `tz` is equivalent to rounding in
// that zone's own minutes, since every real-world zone offset is a multiple
// of 15 minutes.
const nextQuarterHourParts = (tz) => {
  const quarterHourMs = 15 * 60000;
  const rounded = Math.ceil(Date.now() / quarterHourMs) * quarterHourMs;
  return getZonedDateTimeParts(new Date(rounded).toISOString(), tz);
};

// Sort order for the open-tasks list: whichever task the employee is
// currently clocked into first, then late tasks, then planned tasks
// (soonest first), then tasks still waiting on a plan, then everything else.
const taskSortRank = (task, currentTaskId) => {
  if (currentTaskId && String(task.id) === String(currentTaskId)) return 0;
  if (task.is_late) return 1;
  if (task.planned_start) return 2;
  if (task.needs_planning) return 3;
  return 4;
};

const sortOpenTasks = (list, currentTaskId) => [...list].sort((a, b) => {
  const rankA = taskSortRank(a, currentTaskId);
  const rankB = taskSortRank(b, currentTaskId);
  if (rankA !== rankB) return rankA - rankB;
  if (rankA === 2) return new Date(a.planned_start) - new Date(b.planned_start);
  return 0;
});

function TaskCard({ task, onAction, onResume, resuming, onPlan, onClockAction, currentTaskId, timezone, clockActionPending, anyClockActionPending }) {
  const onTheClock = Boolean(currentTaskId) && String(currentTaskId) === String(task.id);
  // What the Clock button on this specific card should do, mirroring how
  // ClockCard itself decides: nothing on the clock → Start; on the clock
  // somewhere else (or on no task) → Switch to this task; on the clock on
  // THIS task → Clock out.
  const clockAction = !currentTaskId ? 'start' : (onTheClock ? 'clockout' : 'switch');
  const clockLabel = clockAction === 'start' ? 'Start' : clockAction === 'switch' ? 'Switch to this task' : 'Clock out';
  const ClockActionIcon = clockAction === 'start' ? StartIcon : clockAction === 'switch' ? SwitchIcon : ClockOutIcon;
  const clockTooltip = clockAction === 'start'
    ? 'Start the clock on this task.'
    : clockAction === 'switch'
      ? 'Save your hours where you are now and start the clock on this task instead.'
      : 'Clock out of this task — opens the usual clock-out screen.';

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">{task.title}</Typography>
          {task.status === 'pending'
            ? (
              <Tooltip title={task.pending_reason || 'Pending'}>
                <Chip size="small" color="warning" label="Pending" />
              </Tooltip>
            )
            : <Chip size="small" color="primary" label="Active" />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {task.affiliate_name || task.company_name || 'No property'}
          {task.due_date && ` · Due ${formatDateDisplay(task.due_date)}`}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
          <Chip size="small" label={PRIORITY_LABELS[task.priority] || task.priority} color={PRIORITY_COLORS[task.priority] || 'default'} />
          {task.planned_start ? (
            <Chip size="small" variant="outlined" label={`Planned ${formatPlannedDateTime(task.planned_start, timezone)}`} />
          ) : task.needs_planning ? (
            <Chip size="small" color="warning" label="Needs a time" />
          ) : null}
          {task.is_late && <Chip size="small" color="error" label="Late" />}
          {onTheClock && <Chip size="small" color="success" label="On the clock" />}
          <Tooltip title="Hours logged against this task from time entries linked to it.">
            <Chip size="small" variant="outlined" label={`${Number(task.hours_logged || 0).toFixed(2)}h logged`} />
          </Tooltip>
        </Stack>
        {task.description && (
          <Typography variant="body2" sx={{ mt: 1.5, whiteSpace: 'pre-wrap' }}>{task.description}</Typography>
        )}
      </CardContent>
      <CardActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Tooltip title={task.planned_start
          ? 'Change when you plan to do this.'
          : 'Pick a date and time you plan to do this — HourTrax reminds you 15 minutes before.'}
        >
          <Button size="small" startIcon={<PlanIcon />} onClick={() => onPlan(task)}>
            {task.planned_start ? 'Change time' : 'Plan it'}
          </Button>
        </Tooltip>
        <Tooltip title={clockTooltip}>
          <span>
            <Button
              size="small"
              startIcon={clockActionPending ? <CircularProgress size={14} /> : <ClockActionIcon />}
              onClick={() => onClockAction(task, clockAction)}
              disabled={anyClockActionPending}
            >
              {clockLabel}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Mark this task done. You can add an optional note about how it went.">
          <Button size="small" startIcon={<CompleteIcon />} onClick={() => onAction('complete', task)}>
            Mark complete
          </Button>
        </Tooltip>
        {task.status === 'pending' ? (
          <Tooltip title="Ready to continue? This puts the task back to Active.">
            <span>
              <Button size="small" startIcon={<ResumeIcon />} onClick={() => onResume(task)} disabled={resuming}>
                Resume
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="Can't finish this right now? Say why — it shows to whoever manages the task.">
            <Button size="small" color="warning" startIcon={<CantFinishIcon />} onClick={() => onAction('pending', task)}>
              Can't finish yet
            </Button>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const employeeId = user?.employeeId;
  const timezone = getUserTimezone();

  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [tasksEnabled, setTasksEnabled] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [completedExpanded, setCompletedExpanded] = useState(false);
  const [completedTasks, setCompletedTasks] = useState(null);
  const [completedLoading, setCompletedLoading] = useState(false);

  const [actionDialog, setActionDialog] = useState(null); // { type: 'complete' | 'pending', task }
  const [actionNote, setActionNote] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  // True while waiting on ClockCard to confirm the clock-out that "Clock out
  // & complete" asked for, before the completion itself is submitted. Holds
  // a teardown function (removes both the success and the error listener
  // registered for that wait) rather than a single listener reference, since
  // there are now two to clean up together.
  const [completingViaClockout, setCompletingViaClockout] = useState(false);
  const pendingClockoutCleanupRef = useRef(null);

  const [resumingId, setResumingId] = useState(null);

  // The employee's current clock-in, mirrored from the ClockCard rendered
  // below so task chips ("On the clock") and each card's Clock button
  // (Start / Switch to this task / Clock out) stay in sync with it.
  const [currentClockIn, setCurrentClockIn] = useState(null);

  const [planDialog, setPlanDialog] = useState(null); // the task being planned
  const [planDate, setPlanDate] = useState('');
  const [planTime, setPlanTime] = useState('');
  const [planSaving, setPlanSaving] = useState(false);
  const [planError, setPlanError] = useState('');

  // The task whose Clock button is mid-tap, shown with a small spinner,
  // until ClockCard reports either a real state change (via
  // handleClockStatusChange) or a failure (hourtrax:clock-task-error). While
  // this is set, EVERY task card's Clock button is disabled (not just the
  // tapped one) — ClockCard itself only handles one clock in/out/switch at a
  // time, so a second card's button would just queue up behind the first
  // rather than doing anything concurrently. The timeout is a safety net for
  // outcomes ClockCard doesn't report at all, e.g. the employee cancelling
  // the Switch Property dialog it opened.
  const [pendingClockTaskId, setPendingClockTaskId] = useState(null);
  const pendingClockTimeoutRef = useRef(null);

  const clearPendingClockAction = useCallback(() => {
    setPendingClockTaskId(null);
    if (pendingClockTimeoutRef.current) {
      clearTimeout(pendingClockTimeoutRef.current);
      pendingClockTimeoutRef.current = null;
    }
  }, []);

  const loadOpenTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getTasks({ mine: true, status: 'open' });
      setTasks(unwrapList(res.data));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load your tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompletedTasks = useCallback(async () => {
    setCompletedLoading(true);
    try {
      const res = await getTasks({ mine: true, status: 'complete' });
      const list = unwrapList(res.data);
      const sorted = [...list].sort((a, b) => new Date(b.completed_at || 0) - new Date(a.completed_at || 0));
      setCompletedTasks(sorted.slice(0, 20));
    } catch {
      setCompletedTasks([]);
    } finally {
      setCompletedLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!employeeId) return;
    getTaskSettings()
      .then((res) => {
        const enabled = !!res.data?.tasks_enabled;
        setTasksEnabled(enabled);
        if (enabled) loadOpenTasks();
      })
      .catch(() => setTasksEnabled(false))
      .finally(() => setSettingsLoaded(true));
  }, [employeeId, loadOpenTasks]);

  // Reusable "mark complete" — shared by the plain Save button and by "Clock
  // out & complete" (which submits it only after the clock-out it asked for
  // actually lands), so the two paths never duplicate the completion call.
  const submitCompletion = useCallback(async (taskId, note) => {
    setActionSaving(true);
    setActionError('');
    try {
      await updateTaskStatus(taskId, { status: 'complete', completion_note: note || undefined });
      setActionDialog(null);
      await loadOpenTasks();
      if (completedExpanded) loadCompletedTasks();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to update the task');
    } finally {
      setActionSaving(false);
    }
  }, [loadOpenTasks, loadCompletedTasks, completedExpanded]);

  // ClockCard reports every clock-state change here (see the card rendered
  // below). The very first report is just its initial status load — skip
  // that one so mount doesn't double-fetch on top of the settings effect
  // above; every report after that is a real start/switch/clock-out, so the
  // list is refreshed to keep "On the clock", Late and hours-logged current.
  const clockStatusInitializedRef = useRef(false);
  const handleClockStatusChange = useCallback((_isClockedIn, clockIn) => {
    setCurrentClockIn(clockIn || null);
    // A real clock-state change means whatever Clock button tap caused it
    // (if any) succeeded.
    clearPendingClockAction();
    if (clockStatusInitializedRef.current) {
      loadOpenTasks();
    } else {
      clockStatusInitializedRef.current = true;
    }
  }, [loadOpenTasks, clearPendingClockAction]);

  // ClockCard reports a task action that didn't go anywhere (busy, on break,
  // no property resolvable, or the clock-in/out call itself failed) here —
  // stop that card's spinner; the error itself is shown on the ClockCard,
  // scrolled into view by ClockCard when it happens.
  useEffect(() => {
    const handler = (e) => {
      if (String(e.detail?.taskId) === String(pendingClockTaskId)) {
        clearPendingClockAction();
      }
    };
    window.addEventListener('hourtrax:clock-task-error', handler);
    return () => window.removeEventListener('hourtrax:clock-task-error', handler);
  }, [pendingClockTaskId, clearPendingClockAction]);

  // If this screen is navigated away from while "Clock out & complete" is
  // still waiting on the clock-out, drop the listener rather than leaving it
  // attached to a component that's gone. Same for a still-pending Clock
  // button spinner's safety-net timeout.
  useEffect(() => () => {
    if (pendingClockoutCleanupRef.current) {
      pendingClockoutCleanupRef.current();
      pendingClockoutCleanupRef.current = null;
    }
    if (pendingClockTimeoutRef.current) {
      clearTimeout(pendingClockTimeoutRef.current);
      pendingClockTimeoutRef.current = null;
    }
  }, []);

  const sortedTasks = useMemo(
    () => sortOpenTasks(tasks, currentClockIn?.task_id),
    [tasks, currentClockIn]
  );

  const handleToggleCompleted = () => {
    const next = !completedExpanded;
    setCompletedExpanded(next);
    if (next && completedTasks === null) loadCompletedTasks();
  };

  const openAction = (type, task) => {
    setActionDialog({ type, task });
    setActionNote('');
    setActionError('');
    setCompletingViaClockout(false);
  };

  const closeActionDialog = () => {
    if (pendingClockoutCleanupRef.current) {
      pendingClockoutCleanupRef.current();
      pendingClockoutCleanupRef.current = null;
    }
    setCompletingViaClockout(false);
    setActionDialog(null);
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    if (actionDialog.type === 'complete') {
      await submitCompletion(actionDialog.task.id, actionNote);
      return;
    }
    if (!actionNote.trim()) {
      setActionError("A reason is required to say you can't finish yet");
      return;
    }
    setActionSaving(true);
    setActionError('');
    try {
      await updateTaskStatus(actionDialog.task.id, { status: 'pending', pending_reason: actionNote });
      setActionDialog(null);
      await loadOpenTasks();
      if (completedExpanded) loadCompletedTasks();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Failed to update the task');
    } finally {
      setActionSaving(false);
    }
  };

  // Ask the ClockCard above to clock out of this task, wait for it to
  // confirm (`hourtrax:task-clocked-out`), then submit the completion with
  // whatever note is already typed. A plain "Save" still completes without
  // touching the clock at all.
  //
  // Two ways this can end without a completion:
  // - The employee cancels the Clock Out dialog: ClockCard reuses
  //   hourtrax:task-clocked-out with `cancelled: true`.
  // - ClockCard's own guard rejects the request before that dialog ever
  //   opens (busy, on break, not actually clocked in on this task) and
  //   dispatches hourtrax:clock-task-error instead — with no dialog and no
  //   task-clocked-out event ever coming, so this must listen for that too
  //   or "Clock out & complete" would spin forever. Its message is shown
  //   here, inside this dialog, since ClockCard's own Alert can be behind
  //   this dialog's backdrop for a request that never even opened it.
  const handleClockOutAndComplete = () => {
    if (!actionDialog) return;
    const taskId = actionDialog.task.id;
    const note = actionNote;
    setActionError('');
    setCompletingViaClockout(true);

    const cleanup = () => {
      window.removeEventListener('hourtrax:task-clocked-out', successHandler);
      window.removeEventListener('hourtrax:clock-task-error', errorHandler);
      pendingClockoutCleanupRef.current = null;
    };
    const successHandler = (e) => {
      if (String(e.detail?.taskId) !== String(taskId)) return;
      cleanup();
      setCompletingViaClockout(false);
      if (e.detail?.cancelled) return; // clock-out never happened — nothing to complete
      submitCompletion(taskId, note);
    };
    const errorHandler = (e) => {
      if (String(e.detail?.taskId) !== String(taskId)) return;
      cleanup();
      setCompletingViaClockout(false);
      setActionError(e.detail?.message || 'Failed to clock out');
    };

    pendingClockoutCleanupRef.current = cleanup;
    window.addEventListener('hourtrax:task-clocked-out', successHandler);
    window.addEventListener('hourtrax:clock-task-error', errorHandler);
    window.dispatchEvent(new CustomEvent('hourtrax:clock-task', { detail: { taskId, action: 'clockout' } }));
  };

  const handleResume = async (task) => {
    setResumingId(task.id);
    setError('');
    try {
      await updateTaskStatus(task.id, { status: 'active' });
      await loadOpenTasks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resume task');
    } finally {
      setResumingId(null);
    }
  };

  // "Plan it" / "Change time": prefill from the existing plan when there is
  // one, otherwise default to today at the next quarter hour — both read in
  // the account's timezone (`timezone`), matching every display on this
  // screen. Using the browser's own local getters here instead would prefill
  // a shifted time whenever the phone's zone differs from the account's.
  const openPlanDialog = (task) => {
    // getZonedDateTimeParts degrades to blank fields rather than throwing on
    // a bad planned_start or timezone — treat that the same as "nothing to
    // prefill" and fall back to the usual new-plan default.
    const zoned = task.planned_start ? getZonedDateTimeParts(task.planned_start, timezone) : null;
    const { date, time } = zoned?.date && zoned?.time ? zoned : nextQuarterHourParts(timezone);
    setPlanDate(date);
    setPlanTime(time);
    setPlanError('');
    setPlanDialog(task);
  };

  const submitPlan = async () => {
    if (!planDialog || !planDate || !planTime) return;
    setPlanSaving(true);
    setPlanError('');
    try {
      // The date/time typed are wall-clock in the account's timezone, not
      // the browser's — converting with plain `new Date()` here would
      // silently save the wrong instant whenever those two zones differ.
      const iso = zonedWallTimeToUtcIso(planDate, planTime, timezone);
      await planTask(planDialog.id, iso);
      setPlanDialog(null);
      await loadOpenTasks();
    } catch (err) {
      setPlanError(err.response?.data?.error || 'Failed to plan this task');
    } finally {
      setPlanSaving(false);
    }
  };

  const clearPlan = async () => {
    if (!planDialog) return;
    setPlanSaving(true);
    setPlanError('');
    try {
      await planTask(planDialog.id, null);
      setPlanDialog(null);
      await loadOpenTasks();
    } catch (err) {
      setPlanError(err.response?.data?.error || 'Failed to clear the planned time');
    } finally {
      setPlanSaving(false);
    }
  };

  // A task card's Clock button just tells the ClockCard above what to do —
  // it owns location capture, the offline queue, and the Switch/Clock-out
  // dialogs, so none of that is duplicated here. Disabling the button (via
  // pendingClockTaskId) until ClockCard reports a state change or a failure
  // — with a timeout fallback for outcomes it never reports, like the
  // employee cancelling a dialog it opened — keeps a double tap from
  // dispatching the same action twice; ClockCard's own busy guard backs this up.
  const handleClockAction = (task, action) => {
    setPendingClockTaskId(task.id);
    if (pendingClockTimeoutRef.current) clearTimeout(pendingClockTimeoutRef.current);
    pendingClockTimeoutRef.current = setTimeout(() => setPendingClockTaskId(null), 5000);
    window.dispatchEvent(new CustomEvent('hourtrax:clock-task', { detail: { taskId: task.id, action } }));
  };

  if (!employeeId) return null;

  if (!settingsLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tasksEnabled) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MyTasksIcon /> My Tasks
        </Typography>
        <Alert severity="info">Tasks aren't turned on for this organization yet.</Alert>
      </Box>
    );
  }

  const clockedInOnThisTask = Boolean(
    actionDialog && currentClockIn?.task_id && String(currentClockIn.task_id) === String(actionDialog.task.id)
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MyTasksIcon /> My Tasks
      </Typography>

      <Box sx={{ mb: 2 }}>
        <ClockCard listenForTaskActions onStatusChange={handleClockStatusChange} />
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : tasks.length === 0 ? (
        <Alert severity="info">You have no open tasks right now.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {sortedTasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onAction={openAction}
              onResume={handleResume}
              resuming={resumingId === task.id}
              onPlan={openPlanDialog}
              onClockAction={handleClockAction}
              currentTaskId={currentClockIn?.task_id}
              timezone={timezone}
              clockActionPending={pendingClockTaskId === task.id}
              anyClockActionPending={Boolean(pendingClockTaskId)}
            />
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          size="small"
          onClick={handleToggleCompleted}
          endIcon={completedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          Recently completed
        </Button>
        <Collapse in={completedExpanded}>
          <Box sx={{ mt: 1.5 }}>
            {completedLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : !completedTasks || completedTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No completed tasks yet.</Typography>
            ) : (
              <Stack spacing={1}>
                {completedTasks.map((task) => (
                  <Card key={task.id} variant="outlined">
                    <CardContent sx={{ py: 1.25, px: 2, '&:last-child': { pb: 1.25 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="medium">{task.title}</Typography>
                        <Tooltip title="Total hours from time entries linked to this task.">
                          <Chip size="small" variant="outlined" label={`${Number(task.hours_logged || 0).toFixed(2)}h logged`} />
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {task.affiliate_name || task.company_name || 'No property'}
                        {/* completed_at is a full timestamp, not a plain date — read in
                            the account's timezone, or a completion late in the evening
                            there shows as the next day (already true in UTC). */}
                        {task.completed_at && ` · Completed ${formatDateInTimezone(task.completed_at, timezone)}`}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        </Collapse>
      </Box>

      {/* Mark complete / Can't finish yet dialog */}
      <Dialog open={Boolean(actionDialog)} onClose={closeActionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {actionDialog?.type === 'complete' ? 'Mark Complete' : "Can't Finish Yet"}
        </DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label={actionDialog?.type === 'complete' ? 'Note (optional)' : 'Reason (required)'}
            value={actionNote}
            onChange={(e) => setActionNote(e.target.value)}
            disabled={completingViaClockout}
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {/* Stays enabled even while "Clock out & complete" is waiting — it
              must always be possible to back out without submitting anything. */}
          <Button onClick={closeActionDialog}>Cancel</Button>
          {actionDialog?.type === 'complete' && clockedInOnThisTask && (
            <Tooltip title="Clocks you out of this task first, then marks it complete with the note above.">
              <span>
                <Button onClick={handleClockOutAndComplete} disabled={actionSaving || completingViaClockout}>
                  {completingViaClockout ? <CircularProgress size={20} /> : 'Clock out & complete'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button variant="contained" onClick={submitAction} disabled={actionSaving || completingViaClockout}>
            {actionSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Plan dialog */}
      <Dialog open={Boolean(planDialog)} onClose={() => setPlanDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Plan this task</DialogTitle>
        <DialogContent>
          {planError && <Alert severity="error" sx={{ mb: 2 }}>{planError}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Date"
              type="date"
              size="small"
              fullWidth
              value={planDate}
              onChange={(e) => setPlanDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Time"
              type="time"
              size="small"
              fullWidth
              value={planTime}
              onChange={(e) => setPlanTime(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 900 }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            HourTrax reminds you 15 minutes before.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Button onClick={() => setPlanDialog(null)} disabled={planSaving}>Cancel</Button>
          {planDialog?.planned_start && (
            <Button color="warning" onClick={clearPlan} disabled={planSaving}>
              Clear time
            </Button>
          )}
          <Button variant="contained" onClick={submitPlan} disabled={planSaving || !planDate || !planTime}>
            {planSaving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
